/**
 * Resource metadata and the loaded-resource contract (Sprint-017.1 FR-6).
 *
 * Metadata is what discovery produces: it is cheap to obtain and must never
 * require reading a resource's payload, so that startup discovery stays
 * scalable to thousands of resources and loading stays lazy.
 */
import { makeResourceId } from './resource-id.js';
/** Version reported for resources that do not declare one. */
export const DEFAULT_RESOURCE_VERSION = '0.0.0';
/**
 * Whether a resource's payload travels as text or as bytes (Sprint 041.0 §8.1).
 *
 * Declared at **discovery**, never sniffed at load: discovery promises to be
 * cheap and to never read a payload, and a provider that had to open a file to
 * learn whether it was text would break that promise for thousands of resources
 * at startup. The kind comes from the format, which a provider already knows
 * from the file extension.
 */
export const RESOURCE_PAYLOAD_KINDS = {
    Text: 'text',
    Binary: 'binary'
};
/**
 * What a resource is assumed to carry when nothing says otherwise.
 *
 * `'text'`, so every resource that existed before Sprint 041.0 keeps its
 * behaviour and a binary read only ever happens for a format that asked for one.
 */
export const DEFAULT_RESOURCE_PAYLOAD_KIND = RESOURCE_PAYLOAD_KINDS.Text;
/** A metadata record's payload kind, defaulting to text. */
export function resourcePayloadKind(metadata) {
    return metadata.payloadKind ?? DEFAULT_RESOURCE_PAYLOAD_KIND;
}
/**
 * Builds metadata with a derived identifier, keeping `id`, `type` and `name`
 * consistent by construction.
 *
 * @throws {ResourceError} `invalid-identifier` when type or name are malformed.
 */
export function createResourceMetadata(options) {
    return {
        id: makeResourceId(options.type, options.name),
        type: options.type,
        name: options.name,
        version: options.version ?? DEFAULT_RESOURCE_VERSION,
        provider: options.provider,
        origin: options.origin,
        payloadKind: options.payloadKind ?? DEFAULT_RESOURCE_PAYLOAD_KIND
    };
}
/** Builds a loaded resource from metadata and a decoded payload. */
export function createResource(metadata, data) {
    return { id: metadata.id, type: metadata.type, metadata, data };
}
//# sourceMappingURL=resource-metadata.js.map